
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TeamEditFormData } from '@/components/admin/team-edit/teamEditSchema';

interface Team {
  id: string;
  name: string;
  club_id: string;
  season_id: string | null;
  division_id?: string | null;
  birth_date_cutoff?: string | null;
}

export const useTeamEdit = (team: Team | null) => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getInitialData = (): TeamEditFormData => {
    if (!team) {
      return {
        name: '',
        club_id: '',
        season_id: '',
        division_id: 'no-division',
        birth_month: '',
        birth_day: '',
        birth_year: '',
      };
    }

    let birthMonth = '';
    let birthDay = '';
    let birthYear = '';

    if (team.birth_date_cutoff) {
      const date = new Date(team.birth_date_cutoff + 'T00:00:00');
      birthMonth = String(date.getMonth() + 1).padStart(2, '0');
      birthDay = String(date.getDate()).padStart(2, '0');
      birthYear = String(date.getFullYear());
    }

    return {
      name: team.name,
      club_id: team.club_id,
      season_id: team.season_id || '',
      division_id: team.division_id || 'no-division',
      birth_month: birthMonth,
      birth_day: birthDay,
      birth_year: birthYear,
    };
  };

  const handleSubmit = async (data: TeamEditFormData) => {
    if (!team) return;

    
    setIsSubmitting(true);
    try {
      // Convert 'no-division' back to null for database
      const divisionIdValue = data.division_id === 'no-division' ? null : data.division_id;
      
      // Format birth date cutoff for database storage
      let birthDateCutoffValue = null;
      if (data.birth_month && data.birth_day && data.birth_year) {
        const month = data.birth_month.padStart(2, '0');
        const day = data.birth_day.padStart(2, '0');
        const year = data.birth_year;
        birthDateCutoffValue = `${year}-${month}-${day}`;
      }
      
      
      const teamData = {
        name: data.name,
        club_id: data.club_id,
        season_id: data.season_id,
        division_id: divisionIdValue,
        birth_date_cutoff: birthDateCutoffValue
      };


      const { data: result, error } = await supabase
        .from('teams')
        .update(teamData)
        .eq('id', team.id)
        .select();

      if (error) {
        throw error;
      }
      
      toast.success('Team updated successfully');
      setIsDialogOpen(false);
      
      // Invalidate and refetch team data
      await queryClient.invalidateQueries({ queryKey: ['team', team.id] });
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error('Failed to update team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  return {
    isDialogOpen,
    setIsDialogOpen,
    isSubmitting,
    initialData: getInitialData(),
    handleSubmit,
    handleDialogClose,
    openDialog,
  };
};
